import React, {useState} from 'react';
import {Layout, Modal, Input, InputNumber, Select, DatePicker, Tag, Switch} from "antd";
import {Button as NButton, Card, Container, Text} from "@nextui-org/react";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, doc, getFirestore, setDoc, updateDoc, serverTimestamp, Timestamp} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";

const db = getFirestore(Firebase);
const auth = getAuth(Firebase);
const {Content} = Layout;

function fmtDate(val: any) {
    if (!val) return '—';
    const d = typeof val.toDate === 'function' ? val.toDate() : val;
    return moment(d).format('DD MMM YYYY, HH:mm');
}

const emptyForm = {
    code: '', discountType: 'percent' as 'fixed' | 'percent', discountValue: undefined as number | undefined,
    maxDiscountAmount: undefined as number | undefined, minOrderValue: undefined as number | undefined,
    maxOrderValue: undefined as number | undefined,
    maxUses: undefined as number | undefined, maxUsesPerBuyer: 1 as number | undefined,
    expiresAt: null as moment.Moment | null,
};

// ── Create/edit modal ────────────────────────────────────────────────────────

function CodeFormModal({open, onClose, editing}: {open: boolean; onClose: () => void; editing: any | null}) {
    const toast = useToast();
    const [form, setForm] = useState<any>(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        if (editing) {
            setForm({
                ...editing,
                expiresAt: editing.expiresAt ? moment(editing.expiresAt.toDate()) : null,
            });
        } else {
            setForm(emptyForm);
        }
    }, [editing, open]);

    const submit = async () => {
        const code = form.code.trim().toUpperCase();
        if (!code || !form.discountValue) {
            toast({position: 'top', status: 'warning', title: 'Code and discount value are required'});
            return;
        }
        setSubmitting(true);
        try {
            await setDoc(doc(db, 'discountCodes', code), {
                code,
                discountType: form.discountType,
                discountValue: form.discountValue,
                maxDiscountAmount: form.maxDiscountAmount ?? null,
                minOrderValue: form.minOrderValue ?? null,
                maxOrderValue: form.maxOrderValue ?? null,
                maxUses: form.maxUses ?? null,
                maxUsesPerBuyer: form.maxUsesPerBuyer ?? null,
                expiresAt: form.expiresAt ? Timestamp.fromDate(form.expiresAt.toDate()) : null,
                status: editing?.status ?? 'active',
                usedCount: editing?.usedCount ?? 0,
                createdAt: editing ? editing.createdAt : serverTimestamp(),
                createdBy: auth.currentUser?.uid ?? '',
            }, {merge: true});
            toast({position: 'top', status: 'success', title: editing ? 'Code updated' : 'Code created'});
            onClose();
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to save', description: e?.message});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} title={editing ? `Edit ${editing.code}` : 'New discount code'} onCancel={onClose} onOk={submit} confirmLoading={submitting} okText="Save">
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                <div>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Code</div>
                    <Input value={form.code} disabled={!!editing} placeholder="e.g. WELCOME10"
                           onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} />
                </div>
                <div style={{display: 'flex', gap: 10}}>
                    <div style={{flex: 1}}>
                        <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Type</div>
                        <Select style={{width: '100%'}} value={form.discountType}
                                onChange={(v) => setForm({...form, discountType: v})}
                                options={[{value: 'percent', label: 'Percent off'}, {value: 'fixed', label: 'Fixed MWK off'}]} />
                    </div>
                    <div style={{flex: 1}}>
                        <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>
                            {form.discountType === 'percent' ? 'Percent (e.g. 10 = 10%)' : 'Amount (MWK)'}
                        </div>
                        <InputNumber style={{width: '100%'}} min={1} value={form.discountValue}
                                     onChange={(v) => setForm({...form, discountValue: v ?? undefined})} />
                    </div>
                </div>
                {form.discountType === 'percent' && (
                    <div>
                        <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>
                            Max discount payout (MWK, optional) — caps how much the discount itself can be worth,
                            e.g. "10% off, but never more than 15,000 MWK off" even on a very large order.
                            This does NOT limit which order sizes the code can be used on.
                        </div>
                        <InputNumber style={{width: '100%'}} min={1} value={form.maxDiscountAmount}
                                     onChange={(v) => setForm({...form, maxDiscountAmount: v ?? undefined})} />
                    </div>
                )}
                <div style={{display: 'flex', gap: 10}}>
                    <div style={{flex: 1}}>
                        <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Minimum order value (MWK, optional)</div>
                        <InputNumber style={{width: '100%'}} min={0} value={form.minOrderValue}
                                     onChange={(v) => setForm({...form, minOrderValue: v ?? undefined})} />
                    </div>
                    <div style={{flex: 1}}>
                        <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Maximum order value (MWK, optional) — code can't be used on orders above this</div>
                        <InputNumber style={{width: '100%'}} min={0} value={form.maxOrderValue}
                                     onChange={(v) => setForm({...form, maxOrderValue: v ?? undefined})} />
                    </div>
                </div>
                <div style={{display: 'flex', gap: 10}}>
                    <div style={{flex: 1}}>
                        <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Total uses allowed (optional = unlimited)</div>
                        <InputNumber style={{width: '100%'}} min={1} value={form.maxUses}
                                     onChange={(v) => setForm({...form, maxUses: v ?? undefined})} />
                    </div>
                    <div style={{flex: 1}}>
                        <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Uses per buyer (optional = unlimited)</div>
                        <InputNumber style={{width: '100%'}} min={1} value={form.maxUsesPerBuyer}
                                     onChange={(v) => setForm({...form, maxUsesPerBuyer: v ?? undefined})} />
                    </div>
                </div>
                <div>
                    <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>Expires (optional)</div>
                    <DatePicker style={{width: '100%'}} showTime value={form.expiresAt}
                                onChange={(v) => setForm({...form, expiresAt: v})} />
                </div>
            </div>
        </Modal>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────

const DiscountCodes = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<any | null>(null);
    const toast = useToast();

    const [snapshot, loading] = useCollection(collection(db, 'discountCodes'));
    const rows = (snapshot?.docs ?? [])
        .map((d) => ({...d.data()}))
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));

    const toggleStatus = async (row: any) => {
        try {
            await updateDoc(doc(db, 'discountCodes', row.code), {
                status: row.status === 'active' ? 'inactive' : 'active',
            });
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to update', description: e?.message});
        }
    };

    const describe = (row: any) => {
        const parts = [row.discountType === 'percent' ? `${row.discountValue}% off` : `MWK ${row.discountValue} off`];
        if (row.maxDiscountAmount) parts.push(`discount capped at MWK ${row.maxDiscountAmount}`);
        if (row.minOrderValue) parts.push(`min order MWK ${row.minOrderValue}`);
        if (row.maxOrderValue) parts.push(`max order MWK ${row.maxOrderValue}`);
        return parts.join(' · ');
    };

    const isExpired = (row: any) => row.expiresAt && row.expiresAt.toMillis() < Date.now();

    return (
        <>
            <Layout style={{minHeight: '100vh'}}>
                <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
                <Layout className="site-layout">
                    <Sidebar collapsed={collapsed}/>
                    <Content>
                        <Container className="mt-2">
                            <Card className="my-2 p-3 font-JosefinSans">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                                    <Text h4 size={23} css={{color: "#29327b", fontWeight: "$bold"}}>
                                        Discount Codes
                                    </Text>
                                    <NButton size="sm" color="primary" onClick={() => { setEditing(null); setModalOpen(true); }}>
                                        + New code
                                    </NButton>
                                </div>

                                {loading ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>Loading…</div>
                                ) : rows.length === 0 ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>No discount codes yet.</div>
                                ) : (
                                    <div style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                                        {rows.map((row) => (
                                            <div key={row.code} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
                                                padding: '12px 16px', border: '1px solid #eee', borderRadius: 8, background: '#fff',
                                            }}>
                                                <div>
                                                    <div style={{fontWeight: 700, fontSize: 14}}>{row.code}</div>
                                                    <div style={{fontSize: 12, color: '#888'}}>{describe(row)}</div>
                                                    <div style={{fontSize: 11, color: '#aaa'}}>
                                                        {row.usedCount ?? 0}{row.maxUses ? ` / ${row.maxUses}` : ''} uses
                                                        {row.expiresAt ? ` · expires ${fmtDate(row.expiresAt)}` : ''}
                                                    </div>
                                                </div>
                                                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                                    {isExpired(row) && <Tag color="default">Expired</Tag>}
                                                    <Tag color={row.status === 'active' ? 'green' : 'default'}>{row.status}</Tag>
                                                    <Switch size="small" checked={row.status === 'active'} onChange={() => toggleStatus(row)} />
                                                    <NButton size="xs" bordered onClick={() => { setEditing(row); setModalOpen(true); }}>Edit</NButton>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </Container>
                    </Content>
                </Layout>
            </Layout>

            <CodeFormModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} />
        </>
    );
};

export default DiscountCodes;
